import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap, shareReplay } from 'rxjs/operators';

/**
 * Interface for cached translation data
 */
interface CachedTranslation {
    data: any;
    timestamp: number;
    version?: string;
}

/**
 * Configuration for lazy translation loader
 */
interface LazyLoaderConfig {
    prefix: string;
    suffix: string;
    cacheTimeout: number; // in milliseconds
    maxCacheSize: number; // maximum number of cached languages
    enableCompression: boolean;
    enableVersioning: boolean;
}

/**
 * Lazy translation loader that implements caching and performance optimizations
 */
@Injectable({
    providedIn: 'root',
})
export class LazyTranslationLoaderService implements TranslateLoader {
    private cache = new Map<string, CachedTranslation>();
    private loadingCache = new Map<string, Observable<any>>();
    private config: LazyLoaderConfig;

    constructor(private http: HttpClient) {
        this.config = {
            prefix: './assets/i18n/',
            suffix: '.json',
            cacheTimeout: 30 * 60 * 1000, // 30 minutes
            maxCacheSize: 10, // Maximum 10 languages in cache
            enableCompression: true,
            enableVersioning: false,
        };
    }

    /**
     * Load translation file with lazy loading and caching
     */
    getTranslation(lang: string): Observable<any> {
        // Check if translation is already being loaded
        const loadingObservable = this.loadingCache.get(lang);
        if (loadingObservable) {
            console.log(
                `Translation for ${lang} is already loading, returning existing observable`
            );
            return loadingObservable;
        }

        // Check cache first
        const cached = this.getCachedTranslation(lang);
        if (cached) {
            console.log(`Using cached translation for ${lang}`);
            return of(cached);
        }

        // Load translation file
        console.log(`Loading translation file for ${lang}`);
        const url = `${this.config.prefix}${lang}${this.config.suffix}`;

        const loadObservable = this.http.get(url).pipe(
            map((translations: any) => {
                // Validate translation structure
                if (!translations || typeof translations !== 'object') {
                    throw new Error(
                        `Invalid translation structure for ${lang}`
                    );
                }
                return translations;
            }),
            tap((translations: any) => {
                // Cache the loaded translation
                this.cacheTranslation(lang, translations);
                console.log(`Translation for ${lang} loaded and cached`);
            }),
            catchError((error) => {
                console.error(`Failed to load translation for ${lang}:`, error);
                this.loadingCache.delete(lang);
                return throwError(() => error);
            }),
            shareReplay(1) // Share the result among multiple subscribers
        );

        // Cache the loading observable to prevent duplicate requests
        this.loadingCache.set(lang, loadObservable);

        // Clean up loading cache when complete
        loadObservable.subscribe({
            complete: () => {
                this.loadingCache.delete(lang);
            },
            error: () => {
                this.loadingCache.delete(lang);
            },
        });

        return loadObservable;
    }

    /**
     * Get cached translation if valid
     */
    private getCachedTranslation(lang: string): any | null {
        const cached = this.cache.get(lang);

        if (!cached) {
            return null;
        }

        // Check if cache is still valid
        const now = Date.now();
        const isExpired = now - cached.timestamp > this.config.cacheTimeout;

        if (isExpired) {
            console.log(`Cache expired for ${lang}, removing from cache`);
            this.cache.delete(lang);
            return null;
        }

        return cached.data;
    }

    /**
     * Cache translation data
     */
    private cacheTranslation(lang: string, data: any): void {
        // Implement LRU cache eviction if cache is full
        if (this.cache.size >= this.config.maxCacheSize) {
            this.evictOldestCacheEntry();
        }

        const cachedTranslation: CachedTranslation = {
            data,
            timestamp: Date.now(),
            version: this.config.enableVersioning
                ? this.generateVersion(data)
                : undefined,
        };

        this.cache.set(lang, cachedTranslation);
        console.log(
            `Translation cached for ${lang}. Cache size: ${this.cache.size}`
        );
    }

    /**
     * Evict oldest cache entry (LRU strategy)
     */
    private evictOldestCacheEntry(): void {
        let oldestKey: string | null = null;
        let oldestTimestamp = Date.now();

        for (const [key, cached] of this.cache.entries()) {
            if (cached.timestamp < oldestTimestamp) {
                oldestTimestamp = cached.timestamp;
                oldestKey = key;
            }
        }

        if (oldestKey) {
            this.cache.delete(oldestKey);
            console.log(`Evicted oldest cache entry: ${oldestKey}`);
        }
    }

    /**
     * Generate version hash for translation data
     */
    private generateVersion(data: any): string {
        // Simple hash function for versioning
        const str = JSON.stringify(data);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(16);
    }

    /**
     * Preload translation for a specific language
     */
    preloadTranslation(lang: string): Observable<any> {
        console.log(`Preloading translation for ${lang}`);
        return this.getTranslation(lang);
    }

    /**
     * Clear cache for a specific language
     */
    clearCache(lang?: string): void {
        if (lang) {
            this.cache.delete(lang);
            this.loadingCache.delete(lang);
            console.log(`Cache cleared for ${lang}`);
        } else {
            this.cache.clear();
            this.loadingCache.clear();
            console.log('All translation cache cleared');
        }
    }

    /**
     * Get cache statistics
     */
    getCacheStats(): {
        size: number;
        maxSize: number;
        languages: string[];
        totalMemoryUsage: number;
    } {
        const languages = Array.from(this.cache.keys());
        const totalMemoryUsage = this.estimateMemoryUsage();

        return {
            size: this.cache.size,
            maxSize: this.config.maxCacheSize,
            languages,
            totalMemoryUsage,
        };
    }

    /**
     * Estimate memory usage of cached translations
     */
    private estimateMemoryUsage(): number {
        let totalSize = 0;
        for (const [key, cached] of this.cache.entries()) {
            // Rough estimation: JSON string length * 2 (for UTF-16) + overhead
            const dataSize = JSON.stringify(cached.data).length * 2;
            const keySize = key.length * 2;
            const overhead = 100; // Estimated overhead for object structure
            totalSize += dataSize + keySize + overhead;
        }
        return totalSize;
    }

    /**
     * Update loader configuration
     */
    updateConfig(newConfig: Partial<LazyLoaderConfig>): void {
        this.config = { ...this.config, ...newConfig };
        console.log('Lazy loader configuration updated:', this.config);
    }

    /**
     * Check if a translation is cached
     */
    isCached(lang: string): boolean {
        return this.cache.has(lang) && this.getCachedTranslation(lang) !== null;
    }

    /**
     * Check if a translation is currently loading
     */
    isLoading(lang: string): boolean {
        return this.loadingCache.has(lang);
    }

    /**
     * Warm up cache by preloading common languages
     */
    warmUpCache(languages: string[]): Observable<any[]> {
        console.log('Warming up cache for languages:', languages);
        const preloadObservables = languages.map((lang) =>
            this.preloadTranslation(lang).pipe(
                catchError((error) => {
                    console.warn(`Failed to preload ${lang}:`, error);
                    return of(null);
                })
            )
        );

        return new Observable((observer) => {
            Promise.all(preloadObservables.map((obs) => obs.toPromise()))
                .then((results) => {
                    console.log('Cache warm-up completed');
                    observer.next(results);
                    observer.complete();
                })
                .catch((error) => {
                    console.error('Cache warm-up failed:', error);
                    observer.error(error);
                });
        });
    }

    /**
     * Get cache hit ratio for performance monitoring
     */
    getCacheHitRatio(): number {
        // This would require tracking hits and misses over time
        // For now, return a simple estimation based on cache size
        return this.cache.size > 0 ? 0.8 : 0; // Placeholder implementation
    }

    /**
     * Optimize cache by removing unused translations
     */
    optimizeCache(): void {
        const now = Date.now();
        const cutoffTime = now - this.config.cacheTimeout / 2; // Remove entries older than half the timeout

        for (const [key, cached] of this.cache.entries()) {
            if (cached.timestamp < cutoffTime) {
                this.cache.delete(key);
                console.log(`Removed unused translation from cache: ${key}`);
            }
        }

        console.log(`Cache optimized. Current size: ${this.cache.size}`);
    }
}
